"use server";

import {
  getDatasetSharedToWorkingGroupMsg,
  getDatasetContentUpdatedMsg,
  getGenericItemSharedToWorkingGroupMsg,
} from "@/components/user_notification/messages";
import prisma from "@/lib/db";
import {
  UserLocalCache,
  UserNotification,
  UserNotificationType,
  WorkingGroupUserStatus,
} from "@prisma/client";
import { verifyServerSession } from "@/utils/auth";
import {
  makeDatasetsSharedToWorkingData,
  makeDatasetContentUpdatedData,
  makeSequencesSharedToWorkingData,
} from "@/utils/userNotification";
import { formatUserName } from "@/utils/userLocalCache";

export const getManyUserNotificationsByCurrentUser = async (
  isRead: boolean,
): Promise<UserNotification[]> => {
  const { loggedInUuid } = await verifyServerSession();
  return prisma.userNotification.findMany({
    where: {
      isRead: isRead,
      user: {
        keycloakId: loggedInUuid,
      },
    },
    orderBy: {
      createdAt: "desc",
    },
  });
};

export const updateUserNotificationReadByCurrentUser = async (
  notificationId: number,
  isRead: boolean,
): Promise<UserNotification> => {
  const { loggedInUuid } = await verifyServerSession();
  return prisma.userNotification.update({
    where: { id: notificationId, user: { keycloakId: loggedInUuid } },
    data: { isRead: isRead },
  });
};

export const countCurrentUserPendingNotificationsByType = async (
  type: UserNotificationType,
): Promise<number> => {
  const { loggedInUuid } = await verifyServerSession();
  return prisma.userNotification.count({
    where: {
      isRead: false,
      type: type,
      user: {
        keycloakId: loggedInUuid,
      },
    },
  });
};


const _notifyAllWorkingGroups_ItemsShared = async (
  workingGroupId: number,
  count: number,
  itemType: "dataset" | "sequence",
) => {
  const { loggedInUuid } = await verifyServerSession();
  const fromUser = await prisma.userLocalCache.findUnique({
    where: { keycloakId: loggedInUuid },
  });

  if (!fromUser) {
    throw new Error("User not found.");
  }

  const workingGroup = await prisma.workingGroup.findUniqueOrThrow({
    select: {
      id: true,
      name: true,
    },
    where: {
      id: workingGroupId,
      users: {
        some: {
          userId: fromUser.id,
          status: WorkingGroupUserStatus.ACCEPTED,
        },
      },
    },
  });

  const members = await prisma.workingGroupUser.findMany({
    select: {
      userId: true,
      user: {
        select: {
          firstName: true,
          lastName: true,
        },
      },
    },
    where: {
      status: WorkingGroupUserStatus.ACCEPTED,
      workingGroupId: workingGroupId,
    },
  });

  return await prisma.userNotification.createMany({
    data: members
      .filter((member) => member.userId !== fromUser.id)
      .map((member) => {
        const userId = member.userId;
        const sharedByUserName = formatUserName(fromUser);

        if (itemType === "dataset") {
          return {
            userId,
            type: UserNotificationType.GENERIC_ITEM_SHARED_TO_WORKING_GROUP,
            message: getDatasetSharedToWorkingGroupMsg(
              sharedByUserName,
              count,
              workingGroup.name,
            ),
            data: makeDatasetsSharedToWorkingData(
              fromUser.id,
              fromUser.firstName,
              fromUser.lastName,
              workingGroup.id,
            ),
          };
        }

        return {
          userId,
          type: UserNotificationType.GENERIC_ITEM_SHARED_TO_WORKING_GROUP,
          message: getGenericItemSharedToWorkingGroupMsg(
            sharedByUserName,
            count,
            workingGroup.name,
            itemType,
          ),
          data: makeSequencesSharedToWorkingData(
            fromUser.id,
            fromUser.firstName,
            fromUser.lastName,
            workingGroup.id,
          ),
        };
      }),
  });
};

export const notifyAllWorkingGroups_DatasetsShared = async (
  workingGroupId: number,
  datasetsNbr: number,
) => {
  return await _notifyAllWorkingGroups_ItemsShared(
    workingGroupId,
    datasetsNbr,
    "dataset",
  );
};

export const notifyAllWorkingGroups_SequencesShared = async (
  workingGroupId: number,
  count: number,
) => {
  return await _notifyAllWorkingGroups_ItemsShared(
    workingGroupId,
    count,
    "sequence",
  );
};


/**
 * Notify all members of the Working Group when datasets are shared with it
 * following a dataset update.
 */
const _notifyAllWorkingGroups_DatasetUpdated = async (
  updatedByUser: UserLocalCache,
  previousDatasetId: number,
  previousDatasetName: string,
  newDatasetId: number,
  newDatasetName: string,
) => {
  const workingGroups = await prisma.workingGroup.findMany({
    select: {
      id: true,
      name: true,
      users: {
        select: {
          userId: true,
        },
        where: {
          status: WorkingGroupUserStatus.ACCEPTED,
        },
      },
    },
    where: {
      datasets: {
        some: {
          datasetId: previousDatasetId,
        },
      },
    },
  });

  if (workingGroups.length === 0) {
    return { count: 0 };
  }

  // Get all members of the working groups
  const allMemberIds = new Set<number>();
  workingGroups.forEach((wg) => {
    wg.users.forEach((user) => {
      if (user.userId !== updatedByUser.id) {
        allMemberIds.add(user.userId);
      }
    });
  });

  if (allMemberIds.size === 0) {
    return { count: 0 };
  }

  const message = getDatasetContentUpdatedMsg(
    formatUserName(updatedByUser),
    previousDatasetName,
    newDatasetName
  );
  const notificationData = makeDatasetContentUpdatedData(
    updatedByUser.id,
    updatedByUser.firstName,
    updatedByUser.lastName,
    previousDatasetId,
    previousDatasetName,
    newDatasetId,
    newDatasetName,
  );

  return await prisma.userNotification.createMany({
    data: Array.from(allMemberIds).map((userId) => ({
      userId,
      type: UserNotificationType.DATASET_CONTENT_UPDATED,
      message: message,
      data: notificationData,
    })),
  });
};

/**
 * Notify the user when a dataset is updated.
 */
export const notifyAllWorkingGroups_DatasetUpdated = async (
  previousDatasetId: number,
  newDatasetId: number,
) => {
  const { loggedInUuid } = await verifyServerSession();
  const fromUser = await prisma.userLocalCache.findUnique({
    where: { keycloakId: loggedInUuid },
  });

  if (!fromUser) {
    throw new Error("User not found.");
  }

  const previousDataset = await prisma.dataset.findUnique({
    where: { id: previousDatasetId },
    select: { name: true },
  });

  const newDataset = await prisma.dataset.findUnique({
    where: { id: newDatasetId },
    select: { name: true },
  });

  if (!previousDataset || !newDataset) {
    throw new Error("Dataset not found.");
  }

  return await _notifyAllWorkingGroups_DatasetUpdated(
    fromUser,
    previousDatasetId,
    previousDataset.name,
    newDatasetId,
    newDataset.name,
  );
};
